import { memo, ReactElement } from "react";
import { useRecoilValue } from "recoil";

import { usePageContext } from "@ryde/redux";
import {
  courseHasUnsavedStationChangeAtom,
  ENTITY_ALLOWED_EDIT_ROUTE_POLYLINE_LIST,
  isLiveMapReducedDisplayMode,
  isTrackingRoute,
  rideHasUnsavedStationChangeAtom,
  StationMarkerVariantEnum,
  stationRouteHasUnsavedStationsChangeAtom,
  TRACKING_ARROW_MIN_ZOOM,
  TRACKING_POINT_MIN_ZOOM,
} from "@ryde/shared";

import { MapLibrePlannedRoute } from "./maplibre-planned-route";
import { IMapLibreRouteResolverProps } from "./maplibre-route-resolver.models";
import { areMapLibreRoutePropsEqual } from "./maplibre-route-resolver.utils";
import { MapLibreTrackingRoute } from "./maplibre-tracking-route";

const MapLibreRouteResolverComponent = ({
  route,
  direction,
  liveMapDisplayMode,
  isCrowdedMap,
  hasUnsavedStationsChange = false,
}: IMapLibreRouteResolverProps): ReactElement => {
  const isLiveMapReduced =
    !!liveMapDisplayMode && isLiveMapReducedDisplayMode(liveMapDisplayMode);
  const { entity } = usePageContext();

  const isEditRoutePolylineAllowed =
    ENTITY_ALLOWED_EDIT_ROUTE_POLYLINE_LIST.includes(entity);

  /*
   * The GPS trail's decorations, and only the trail's. Its geometry is re-indexed on every tracking poll, so
   * on a crowded map its arrows and dots wait for a zoom where they are legible — which is also where they
   * stop being re-laid out for every trail on screen. Selections below the crowded threshold pay neither
   * gate: those are the views where direction is actually read, and a handful of trails costs nothing.
   *
   * The planned route is deliberately NOT gated with them. Its geometry is static — laid out once per tile,
   * not on every poll — and its arrows share the line's own source, so they cost close to nothing. Gating
   * them cost far more than it saved: a multi-ride selection auto-fits to a metro view well below this
   * threshold, so the arrows were missing at the only zoom that selection is ever looked at, and crowded
   * mode had already traded the numbered pins for identical dots. Losing both cues to a route's direction
   * at once reads as a broken map, not a simpler one.
   */
  const trailArrowsMinZoom = isCrowdedMap ? TRACKING_ARROW_MIN_ZOOM : undefined;
  const trailDotsMinZoom = isCrowdedMap ? TRACKING_POINT_MIN_ZOOM : undefined;

  if (isTrackingRoute(route)) {
    return (
      <MapLibreTrackingRoute
        route={route}
        showCarPositionOnly={isLiveMapReduced}
        showTrackingTrail={!isLiveMapReduced}
        arrowsMinZoom={trailArrowsMinZoom}
        dotsMinZoom={trailDotsMinZoom}
      />
    );
  }

  if (isLiveMapReduced) {
    return (
      <MapLibrePlannedRoute
        route={route}
        direction={direction}
        stationMarkerVariant={StationMarkerVariantEnum.dot}
        isCrowdedMap={isCrowdedMap}
      />
    );
  }

  if (
    route.routePolyline &&
    (!hasUnsavedStationsChange || route.isManualPolylineEdit)
  ) {
    return (
      <MapLibrePlannedRoute
        route={route}
        direction={direction}
        isEditRoutePolylineAllowed={isEditRoutePolylineAllowed}
        isCrowdedMap={isCrowdedMap}
      />
    );
  }

  // Stations changed (or no polyline): ignore stale encoded geometry — OSRM preview follows current locations
  // (Leaflet twin uses RoutingMachine in the same case).
  return (
    <MapLibrePlannedRoute
      route={{ ...route, routePolyline: undefined }}
      direction={direction}
      showPlannedPath={Boolean(route.locations?.length)}
      isEditRoutePolylineAllowed={isEditRoutePolylineAllowed}
      isCrowdedMap={isCrowdedMap}
    />
  );
};

const MapLibreRouteResolverMemo = memo(
  MapLibreRouteResolverComponent,
  areMapLibreRoutePropsEqual,
);

/** Reads unsaved-station atoms outside memo so polyline drop re-renders when only that flag flips. */
export const MapLibreRouteResolver = (
  props: Omit<IMapLibreRouteResolverProps, "hasUnsavedStationsChange">,
): ReactElement => {
  const { entity } = usePageContext();
  const stationRouteHasUnsavedStationChange = useRecoilValue(
    stationRouteHasUnsavedStationsChangeAtom,
  );
  const courseHasUnsavedStationChange = useRecoilValue(
    courseHasUnsavedStationChangeAtom,
  );
  const rideHasUnsavedStationChange = useRecoilValue(
    rideHasUnsavedStationChangeAtom,
  );

  let hasUnsavedStationsChange = false;
  if (entity === "stationRoute") {
    hasUnsavedStationsChange = stationRouteHasUnsavedStationChange;
  } else if (entity === "course") {
    hasUnsavedStationsChange = courseHasUnsavedStationChange;
  } else if (entity === "ride" || entity === "rideMap") {
    hasUnsavedStationsChange = rideHasUnsavedStationChange;
  }

  return (
    <MapLibreRouteResolverMemo
      {...props}
      hasUnsavedStationsChange={hasUnsavedStationsChange}
    />
  );
};
